/**
 * Unified Platform Style Definition (Android Material Design 3 / Material UI Primary).
 * Serves as the primary unified design schema across all platforms (Android, Web, iOS).
 * Follows Google Material Design 3 specifications with adaptive elevation,
 * tonal surfaces, and high-contrast typography adhering to WCAG AAA standards.
 *
 * SPECIFICATION & CONTRAST RESOLUTION:
 * - Font forecolour contrasts cleanly with background colours in both Light and Dark modes.
 * - Light mode: High-contrast onSurface charcoal ink (#1D1B20) on light surfaces (>15:1 contrast).
 * - Dark mode: High-contrast onSurface light text (#E6E1E5) on dark surfaces (>13:1 contrast),
 *   eliminating the unreadable black-on-dark issue.
 * - Headers: Blue Linen wash (#DCE3F2) with deep navy ink (#161F36) in light mode,
 *   and slate navy (#20293D) with crisp ivory (#E6E1E5) in dark mode.
 */

import { BrandColors, BlueLinenRange } from "./base";
import { type IPlatformTheme } from "./types";

const tintColorLight = BrandColors.lemonYellow.hex;
const tintColorDark = BrandColors.lemonYellow.hex;

export const AndroidTheme: IPlatformTheme = {
  name: "android",
  light: {
    // --- Standard Material UI / Material Design 3 (M3) Tokens ---
    primary: BlueLinenRange.archival, // #4A608F M3 Primary
    onPrimary: "#FFFFFF", // M3 On Primary
    primaryContainer: BlueLinenRange.wash, // #DCE3F2 M3 Primary Container / Blue Linen wash
    onPrimaryContainer: BlueLinenRange.deepInk, // #161F36 M3 On Primary Container / Deep ink
    secondary: "#5B5E66", // M3 Secondary
    onSecondary: "#FFFFFF",
    secondaryContainer: "#DFE2EB", // M3 Secondary Container
    onSecondaryContainer: "#181B22",
    surface: "#FDFBF7", // M3 Surface
    onSurface: "#1D1B20", // M3 On Surface (Deep charcoal ink, 15:1 contrast against #FDFBF7)
    surfaceVariant: "#E7E0EC", // M3 Surface Variant
    onSurfaceVariant: "#49454F", // M3 On Surface Variant
    surfaceContainer: "#F3EFEA", // M3 Surface Container
    surfaceContainerHigh: "#ECE8E2", // M3 Surface Container High
    outline: "#79747E", // M3 Outline
    outlineVariant: "#CAC4D0", // M3 Outline Variant
    error: "#B3261E", // M3 Standard Error
    onError: "#FFFFFF",
    errorContainer: "#F9DEDC",
    onErrorContainer: "#410E0B",
    inverseSurface: "#313033",
    inverseOnSurface: "#F4EFF4",
    inversePrimary: BlueLinenRange.soft,

    // --- Core Semantic Tokens (Aligned with M3) ---
    text: "#1D1B20", // High-contrast onSurface charcoal ink
    textMuted: "#49454F", // onSurfaceVariant
    textSecondary: "#5B5E66", // secondary
    background: "#FDFBF7", // surface
    backgroundElement: "#F3EFEA", // surfaceContainer
    backgroundSelected: "#DFE2EB", // secondaryContainer
    card: "#FFFFFF", // surfaceContainerLowest / clean white card
    border: "#CAC4D0", // outlineVariant
    tint: tintColorLight,
    tintInactive: "#79747E", // outline
    icon: "#49454F", // onSurfaceVariant
    tabIconDefault: "#79747E",
    tabIconSelected: BlueLinenRange.archival,
    button: BlueLinenRange.archival, // primary
    buttonSecondary: "#DFE2EB", // secondaryContainer
    buttonSecondaryBorder: BlueLinenRange.border, // #BAC4DF Light Blue Linen border
    buttonSecondaryText: "#181B22", // onSecondaryContainer
    buttonDisabled: "rgba(29, 27, 32, 0.12)", // standard M3 disabled fill
    buttonText: "#FFFFFF", // onPrimary
    buttonHoverBorder: BrandColors.lemonYellow.hex, // #FFF24D Lemon Yellow (per design spec)
    buttonSecondaryHoverBorder: BlueLinenRange.archival,
    buttonDangerHoverBorder: BrandColors.beetRed.hex,
    buttonDangerText: BrandColors.beetRed.hex, // #AB1717 Beet Red (5.6:1 contrast against Petal Pink)
    danger: BrandColors.petalPink.hex, // #FFB6A9 Petal Pink per design spec
    header: BlueLinenRange.wash, // #DCE3F2 Blue Linen wash
    headerText: BlueLinenRange.deepInk, // #161F36 Deep archival navy ink (10.5:1 contrast against #DCE3F2)
    inputEditable: "#FFFFFF", // clean card surface for crisp text contrast
    inputEditableBorder: "#79747E", // M3 Outline
    inputReadOnly: "#E7E0EC", // M3 Surface Variant
    inputReadOnlyBorder: "#CAC4D0", // M3 Outline Variant
    inputReadOnlyText: "#49454F", // M3 On Surface Variant
    inputText: "#1D1B20", // High-contrast onSurface charcoal ink
    inputPlaceholder: "#79747E",
    featuredHighlight: BrandColors.seedGreen.hex,
    featuredHighlightBg: "rgba(202, 210, 60, 0.18)",
    transparent: "transparent",
    iconBackground: "transparent",
  },
  dark: {
    // --- Standard Material UI / Material Design 3 (M3) Tokens (Dark) ---
    primary: BlueLinenRange.soft, // #8FA2CF
    onPrimary: BlueLinenRange.deepInk,
    primaryContainer: "#20293D", // Slate navy header surface
    onPrimaryContainer: BlueLinenRange.border,
    secondary: "#C3C6CF",
    onSecondary: "#2D3137",
    secondaryContainer: "#43474E",
    onSecondaryContainer: "#DFE2EB",
    surface: "#141218",
    onSurface: "#E6E1E5", // M3 On Surface (High-contrast light text, 13.5:1 contrast against #141218)
    surfaceVariant: "#49454F",
    onSurfaceVariant: "#CAC4D0",
    surfaceContainer: "#211F26",
    surfaceContainerHigh: "#2B2930",
    outline: "#938F99",
    outlineVariant: "#49454F",
    error: "#F2B8B5",
    onError: "#601410",
    errorContainer: "#8C1D18",
    onErrorContainer: "#F9DEDC",
    inverseSurface: "#E6E1E5",
    inverseOnSurface: "#313033",
    inversePrimary: BlueLinenRange.archival,

    // --- Core Semantic Tokens (Aligned with M3 Dark) ---
    text: "#E6E1E5", // High-contrast onSurface light text
    textMuted: "#CAC4D0", // onSurfaceVariant
    textSecondary: "#C3C6CF",
    background: "#141218", // surface
    backgroundElement: "#211F26", // surfaceContainer
    backgroundSelected: "#43474E", // secondaryContainer
    card: "#211F26", // surfaceContainer
    border: "#49454F", // outlineVariant
    tint: tintColorDark,
    tintInactive: "#938F99",
    icon: "#CAC4D0",
    tabIconDefault: "#938F99",
    tabIconSelected: tintColorDark,
    button: BlueLinenRange.soft,
    buttonSecondary: "#20293D",
    buttonSecondaryBorder: "rgba(186, 196, 223, 0.4)", // Luminous light blue linen border
    buttonSecondaryText: BlueLinenRange.border,
    buttonDisabled: "rgba(230, 225, 229, 0.12)",
    buttonText: BlueLinenRange.deepInk,
    buttonHoverBorder: BrandColors.lemonYellow.hex, // #FFF24D Lemon Yellow (per design spec)
    buttonSecondaryHoverBorder: BlueLinenRange.border,
    buttonDangerHoverBorder: BrandColors.beetRed.hex,
    buttonDangerText: "#410002", // Deep Beet Red ink on Petal Pink
    danger: BrandColors.petalPink.hex, // #FFB6A9 Petal Pink per design spec
    header: "#20293D", // Deep slate navy surface
    headerText: "#E6E1E5", // High-contrast ivory text (9.8:1 contrast against #20293D)
    inputEditable: "#2B2930", // Proper dark container surface
    inputEditableBorder: "#938F99",
    inputReadOnly: "#1D1B20",
    inputReadOnlyBorder: "#49454F",
    inputReadOnlyText: "#CAC4D0",
    inputText: "#E6E1E5", // High-contrast onSurface light text in dark mode
    inputPlaceholder: "#938F99",
    featuredHighlight: BrandColors.seedGreen.hex,
    featuredHighlightBg: "rgba(202, 210, 60, 0.22)",
    transparent: "transparent",
    iconBackground: "transparent",
  },
};
